import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { RiserDiagram, Symbol } from "@/components/RiserDiagram";
import { RISER_DIAGRAMS, SYMBOL_LEGEND } from "@/data/riserDiagrams";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function RiserDiagrams() {
  useDocumentMeta({
    title: "Interactive Riser Diagrams — Lab",
    description: "Clickable schematic riser diagrams covering small commercial, high-rise, healthcare, and mixed-use residential buildings — every device tied to NFPA 72 citations and curriculum lessons.",
    path: "/lab/risers",
  });
  const [activeId, setActiveId] = useState(RISER_DIAGRAMS[0].id);
  const active = RISER_DIAGRAMS.find((d) => d.id === activeId);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="riser-page">
        <div className="overline mb-3"><span className="red-bar" />Lab · Riser Diagrams</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mb-3">
          Building riser diagrams.
        </h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-7">
          Schematic-style risers across 4 common occupancy types. Click any device for a citation,
          a design note, and a jump to the related lesson.
        </p>

        {/* Building selector */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="riser-selector">
          {RISER_DIAGRAMS.map((d) => (
            <button
              key={d.id}
              onClick={() => setActiveId(d.id)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-2 ${
                activeId === d.id
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-red-600 hover:text-red-600"
              }`}
              data-testid={`riser-pick-${d.id}`}
            >
              {d.short}
            </button>
          ))}
        </div>

        {/* Active diagram */}
        <RiserDiagram diagram={active} />

        {/* Legend */}
        <div className="mt-8 card-tech p-5" data-testid="riser-legend">
          <div className="overline mb-3"><span className="red-bar" />Symbol legend</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs text-zinc-700">
            {SYMBOL_LEGEND.map((s) => (
              <div key={s.type} className="flex items-center gap-3 border border-zinc-100 px-2.5 py-2" data-testid={`legend-item-${s.type}`}>
                <svg width="36" height="36" viewBox="0 0 36 36" className="flex-shrink-0">
                  <Symbol type={s.type} cx={18} cy={18} active={false} />
                </svg>
                <div className="leading-tight min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{s.type}</div>
                  <div className="text-zinc-900 font-medium truncate">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
            Schematic style — symbols are simplified for clarity. Real plan-set riser diagrams use
            NFPA 170 standard symbols and additional callouts (wire counts, conduit runs, classification).
          </p>
        </div>

        <div className="mt-8 text-sm text-zinc-600">
          <Link to="/lab" className="hover:text-red-600" data-testid="riser-back-link">
            ← Back to lab
          </Link>
        </div>
      </main>
    </div>
  );
}
